/** Test: chat as a customer would, and see for every answer what really happened. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesLeft, faAnglesRight, faDatabase, faListCheck, faUserTie } from '@fortawesome/free-solid-svg-icons';
import StudioComposer from '../StudioComposer';
import { newTestSessionId, streamEvents, studioApi, studioUrl } from './studioApi';
import ConversationPicker from './ConversationPicker';
import ConversationSidebar, { useConversationSide } from './ConversationSidebar';
import { ScrollDownButton, useChatScroll } from './scrollDown';
import { BotMessage, UserMessage } from './ChatMessage';
import { PanelTabs } from './ui';
import DataPanel from './DataPanel';
import TraceView, { VerdictBadge } from './TraceView';

const PENDING = '__pending__';

function readSession(botId) {
  try { return localStorage.getItem(`agentStudio.test.${botId}`) || ''; } catch (_) { return ''; }
}
function writeSession(botId, sid) {
  try { localStorage.setItem(`agentStudio.test.${botId}`, sid); } catch (_) { /* storage unavailable */ }
}

export default function TestView({ botId, spec, dataVersion }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(() => readSession(botId) || newTestSessionId());
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [live, setLive] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [panel, setPanel] = useState('trace');
  const [mobileTrace, setMobileTrace] = useState(false);
  // the details panel collapses like "Your agent" in Build (Robert, 18.10.2026); closed unless the owner opened it.
  // Clicking an answer to see what happened opens it without storing that.
  const sideKey = `agentStudio.testPanelOpen.${botId}`;
  const [sideOpen, setSideOpen] = useState(() => {
    try { return localStorage.getItem(sideKey) === '1'; } catch (_) { return false; }
  });
  const toggleSide = (open) => {
    setSideOpen(open);
    try { localStorage.setItem(sideKey, open ? '1' : '0'); } catch (_) { /* storage unavailable */ }
  };
  const showPanel = (key) => { setPanel(key); setSideOpen(true); };
  const [input, setInput] = useState('');
  const [actionError, setActionError] = useState('');
  const [loadedSession, setLoadedSession] = useState(null);
  const fresh = useRef(new Set());

  const loadSessions = useCallback(
    () => studioApi.get(botId, '/tests').then((r) => setSessions(r.sessions || [])).catch(() => setSessions([])),
    [botId],
  );
  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    writeSession(botId, sessionId);
    setSelected(null);
    if (fresh.current.has(sessionId)) {
      setMessages([]);
      setLoadedSession(sessionId);
      return undefined;
    }
    let alive = true;
    setLoadedSession(null);
    studioApi.get(botId, `/test/${encodeURIComponent(sessionId)}`)
      .then((r) => {
        if (!alive) return;
        setMessages(r.messages || []);
        const lastA = [...(r.messages || [])].reverse().find((m) => m.role === 'assistant');
        setSelected(lastA?.id ?? null);
        setLoadedSession(sessionId);
      })
      .catch(() => { if (alive) { setMessages([]); setLoadedSession(sessionId); } });
    return () => { alive = false; };
  }, [botId, sessionId]);

  const scroll = useChatScroll(loadedSession === sessionId ? sessionId : null, [messages]);

  const newConversation = () => {
    const sid = newTestSessionId();
    fresh.current.add(sid);
    setSessionId(sid);
    setMessages([]);
  };

  // "New conversation" is always there; in an empty new conversation nothing happens (as in ChatGPT)
  const startNew = () => { if (messages.length) newConversation(); };

  const removeConversations = async (sids) => { // confirmed in the picker (platform modal)
    setActionError('');
    let failed = null;
    for (const sid of sids) {
      try {
        await studioApi.del(botId, `/test/${encodeURIComponent(sid)}`); // eslint-disable-line no-await-in-loop
      } catch (e) {
        failed = failed || e;
      }
    }
    await loadSessions();
    if (sids.includes(sessionId)) newConversation();
    if (failed) setActionError(failed.message || String(failed));
  };

  // the conversation list on the left (ConversationSidebar): an own name, and the search in names and messages
  const [sideListOpen, toggleSideList] = useConversationSide();
  const renameConversation = async (sid, title) => {
    setActionError('');
    try {
      const r = await studioApi.patch(botId, `/test/${encodeURIComponent(sid)}`, { title });
      setSessions((list) => list.map((x) => (x.session_id === sid ? { ...x, title: r.title, named: r.named } : x)));
      return r;
    } catch (e) {
      setActionError(e.message || String(e));
      return null;
    }
  };
  const searchConversations = useCallback(
    (q) => studioApi.get(botId, `/tests/search?q=${encodeURIComponent(q)}`).then((r) => r.sessions || []), [botId]);

  const patchMsg = (id, fn) => setMessages((ms) => ms.map((m) => (m.id === id ? fn(m) : m)));

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    const now = Date.now();
    setMessages((ms) => [
      ...ms,
      { id: `u_${now}`, role: 'user', text },
      { id: PENDING, role: 'assistant', text: '', pending: true, status: t('agent_studio_test_reading') },
    ]);
    setSelected(PENDING);
    setLive({ steps: [], skills: [] });
    setStreaming(true);
    let finalId = null;
    try {
      await streamEvents(studioUrl(botId, '/test'), {
        method: 'POST', body: { session_id: sessionId, text, ui_lang: lang },
      }, (ev) => {
        switch (ev.type) {
          case 'status':
            if (ev.status?.label && ev.status.phase !== 'done') patchMsg(PENDING, (m) => ({ ...m, status: ev.status.label }));
            break;
          case 'route':
            setLive((l) => (l ? { ...l, skills: ev.skills || [] } : l));
            break;
          case 'step':
            setLive((l) => (l ? { ...l, steps: [...l.steps, ev.step] } : l));
            break;
          case 'text':
            patchMsg(PENDING, (m) => ({ ...m, text: m.text + ev.delta }));
            break;
          case 'done':
            finalId = ev.message.id;
            setMessages((ms) => ms.map((m) => (m.id === PENDING
              ? { id: ev.message.id, role: 'assistant', text: ev.message.text, trace: ev.message.trace,
                products: ev.message.products || [],
                verifying: !!spec?.settings?.verify_answers && !ev.message.trace?.paused }
              : m)));
            setSelected(ev.message.id);
            setLive(null);
            break;
          case 'verification':
            patchMsg(ev.message_id, (m) => ({
              ...m, verifying: false, trace: { ...(m.trace || {}), verification: ev.verification, metrics: ev.metrics },
            }));
            break;
          case 'error':
            patchMsg(PENDING, (m) => ({ ...m, text: m.text || t('agent_studio_test_error', { error: ev.text }), pending: false }));
            break;
          default:
            break;
        }
      });
    } catch (e) {
      patchMsg(PENDING, (m) => ({ ...m, text: m.text || t('agent_studio_test_interrupted', { error: e.message }), pending: false }));
    } finally {
      setStreaming(false);
      setLive(null);
      if (finalId) patchMsg(finalId, (m) => ({ ...m, verifying: false }));
      fresh.current.delete(sessionId);
      loadSessions();
      try { window.dispatchEvent(new CustomEvent('ub:usage-updated')); } catch (_) { /* old browsers */ }
    }
  };

  // "Check answer" in "What happened?": an answer the automatic check skipped (setting off), checked on demand
  const checkAnswer = async (id) => {
    setActionError('');
    patchMsg(id, (m) => ({ ...m, verifying: true }));
    try {
      const r = await studioApi.post(botId, `/test/${encodeURIComponent(sessionId)}/check/${id}`, { ui_lang: lang });
      patchMsg(id, (m) => ({ ...m, verifying: false, trace: r.trace || m.trace }));
    } catch (e) {
      patchMsg(id, (m) => ({ ...m, verifying: false }));
      setActionError(e.message || String(e));
    }
  };

  const sel = messages.find((m) => m.id === selected);
  const selTrace = selected === PENDING && live
    ? { steps: [{ type: 'route', skills: live.skills, ok: true }, ...live.steps], llm_calls: [] }
    : (sel?.trace ?? null);

  return (
    <div className="as-test has-side">
      <ConversationSidebar
        title={t('agent_studio_test_conversations')}
        conversations={sessions.map((x) => ({ id: x.session_id, title: x.title, updated_at: x.updated_at, named: x.named }))}
        currentId={sessionId}
        onOpen={(id) => setSessionId(id)}
        onNew={startNew}
        onDelete={(sid) => removeConversations([sid])}
        onRename={renameConversation}
        onSearch={searchConversations}
        open={sideListOpen}
        onToggle={toggleSideList}
      />
      <div className={`as-test-chat ${mobileTrace ? 'is-hidden-mobile' : ''}`}>
        <div className="as-chat-head">
          <ConversationPicker
            conversations={sessions.map((x) => ({ id: x.session_id, title: x.title, updated_at: x.updated_at }))}
            currentId={sessionId}
            onOpen={(id) => setSessionId(id)}
            onNew={startNew}
            onDelete={(sid) => removeConversations([sid])}
            onDeleteMany={removeConversations}
            newDisabled={false}
            menuTitle={t('agent_studio_test_conversations')}
          />
          <button type="button" className="as-btn as-btn-ghost as-btn-sm as-show-mobile as-push" onClick={() => setMobileTrace(true)}>
            {t('agent_studio_test_details')}
          </button>
        </div>

        <div className="as-scroll-wrap">
        <div className="as-test-messages" ref={scroll.ref} onScroll={scroll.onScroll}>
          <div className="as-test-inner">
            {actionError && <div className="as-error">{t('agent_studio_action_failed', { error: actionError })}</div>}
            <BotMessage text={spec?.greeting || t('agent_studio_test_greeting')} />
            {messages.map((m) => (m.role === 'user'
              ? <UserMessage key={m.id} text={m.text} />
              : (
                <AssistantMessage key={m.id} m={m} selected={selected === m.id}
                  onSelect={() => { setSelected(m.id); showPanel('trace'); }}
                  onDetails={() => { setSelected(m.id); showPanel('trace'); setMobileTrace(true); }} />
              )))}
            <div ref={scroll.spacerRef} className="as-scroll-spacer" />
          </div>
        </div>
        <ScrollDownButton show={scroll.show} onClick={scroll.scrollToBottom} />
        </div>

        <div className="as-dock">
          <StudioComposer value={input} onChange={setInput} onSubmit={send} disabled={streaming}
            placeholder={t('agent_studio_test_placeholder')} voiceUrl={studioUrl(botId, '/transcribe')} />
          <div className="as-composer-foot"><span>{t('agent_studio_test_as_customer')}</span></div>
        </div>
      </div>

      <aside className={`as-test-side ${sideOpen ? '' : 'is-collapsed'} ${mobileTrace ? '' : 'is-hidden-mobile'}`}>
        <div className={`as-agent-panel as-test-panel ${sideOpen ? '' : 'is-collapsed'}`}>
          {/* collapsed: a slim rail that names both tabs; a click opens the panel on that tab */}
          <nav className="as-panel-rail" aria-label={t('agent_studio_test_details')}>
            <div className="as-rail-head">
              <button type="button" className="as-rail-open" onClick={() => toggleSide(true)}
                aria-label={t('agent_studio_test_details_show')} title={t('agent_studio_test_details_show')}>
                <span className="as-rail-toggle" aria-hidden="true"><FontAwesomeIcon icon={faAnglesLeft} /></span>
                <span className="as-rail-title">{t('agent_studio_test_details')}</span>
              </button>
            </div>
            {[{ key: 'trace', icon: faListCheck, label: t('agent_studio_what_happened') },
              { key: 'data', icon: faDatabase, label: t('agent_studio_data_tab') }].map((r) => (
              <button key={r.key} type="button" className="as-rail-item" onClick={() => { setPanel(r.key); toggleSide(true); }}>
                <span className="as-rail-icon"><FontAwesomeIcon icon={r.icon} /></span>
                <span className="as-rail-label">{r.label}</span>
              </button>
            ))}
          </nav>
          <div className="as-panel-full">
            <PanelTabs active={panel} onPick={setPanel}
              tabs={[{ key: 'trace', label: t('agent_studio_what_happened') }, { key: 'data', label: t('agent_studio_data_tab') }]}
              before={<button type="button" className="as-show-mobile as-back" onClick={() => setMobileTrace(false)}>← {t('agent_studio_test_back')}</button>}
              lead={(
                <button type="button" className="as-panel-collapse as-tabs-lead" onClick={() => toggleSide(false)}
                  aria-label={t('agent_studio_panel_close')} title={t('agent_studio_panel_close')}>
                  <FontAwesomeIcon icon={faAnglesRight} />
                </button>
              )} />
            <div className="as-side-body">
              {panel === 'trace'
                ? <TraceView trace={selTrace} verifying={sel?.verifying} live={selected === PENDING}
                    onCheck={sel && !streaming ? () => checkAnswer(sel.id) : undefined} />
                : <DataPanel botId={botId} version={dataVersion} />}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

/** Under the answer only the automatic check: a loader while it runs, then the verdict. The details are
 *  in the right panel; clicking another answer or any verdict shows that answer's details there
 *  (the verdict also brings "What happened?" to the front, on a phone it opens the panel). */
/** The chat went to a person (request_live_agent): it now waits in the owner's Live sessions, as for a visitor. */
function HandoffNote({ waiting }) {
  const { t } = useTranslation();
  return (
    <div className={`as-note as-handoff${waiting ? ' is-waiting' : ''}`}>
      <FontAwesomeIcon icon={faUserTie} />
      <span>
        {t(waiting ? 'agent_studio_handoff_waiting' : 'agent_studio_handoff_requested')}{' '}
        <Link to="/live" className="as-link">{t('agent_studio_handoff_open_live')}</Link>
      </span>
    </div>
  );
}

function AssistantMessage({ m, selected, onSelect, onDetails }) {
  const { t } = useTranslation();
  const tr = m.trace;
  if (tr?.paused === 'live_agent') return <HandoffNote waiting />;
  const handoff = !m.pending && (tr?.steps || []).some((s) => s.name === 'request_live_agent' && s.ok);
  const verdict = !m.pending && (m.verifying || tr?.verification) ? (
    <button type="button" className="as-msg-verdict" onClick={onDetails}
      title={t('agent_studio_what_happened')}>
      <VerdictBadge v={tr?.verification} verifying={m.verifying} />
    </button>
  ) : null;
  const selectable = !selected && !m.pending && !!tr;
  const below = verdict ? <span className="as-msg-below">{verdict}</span> : null;
  return (
    <BotMessage className={selected ? 'is-selected' : selectable ? 'is-selectable' : ''}
      below={handoff ? <>{below}<HandoffNote /></> : below}
      products={m.pending ? null : m.products}
      text={m.text} onClick={selectable ? onSelect : undefined}>
      {m.pending && !m.text
        ? <span className="as-pending"><span className="as-focus-pulse" /><span className="as-pending-label">{m.status}</span></span>
        : null}
    </BotMessage>
  );
}
